import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, EyeOff, Building2, Briefcase } from 'lucide-react';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const LandingRegisterModal = ({ open, onOpenChange, onLoginClick, initialRole = 'business' }) => {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { t } = useTranslation();

  const [role, setRole] = useState(initialRole);
  const [registerData, setRegisterData] = useState({
    email: '', password: '', first_name: '', last_name: '', company_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Sync role when the modal is opened with a different initialRole
  useEffect(() => {
    if (open) setRole(initialRole);
  }, [open, initialRole]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await register({ ...registerData, role });
      toast.success(t('auth.accountCreated'));
      onOpenChange(false);
      setTimeout(() => navigate(userData.role === 'admin' ? '/admin/dashboard' : '/dashboard'), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{t('landing.registerModal.title')}</DialogTitle>
        </DialogHeader>

        {/* Role toggle */}
        <div className="flex rounded-lg border border-border p-1 gap-1 bg-secondary/30">
          <button
            type="button"
            data-testid="role-toggle-business"
            onClick={() => setRole('business')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              role === 'business'
                ? 'bg-white text-primary shadow-sm border border-primary/20'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Building2 className="w-4 h-4" />
            {t('landing.registerModal.roleSelect.business')}
          </button>
          <button
            type="button"
            data-testid="role-toggle-vendor"
            onClick={() => setRole('vendor')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              role === 'vendor'
                ? 'bg-white text-primary shadow-sm border border-primary/20'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            {t('landing.registerModal.roleSelect.vendor')}
          </button>
        </div>

        <button
          type="button"
          data-testid="google-signup-btn"
          onClick={async () => {
            sessionStorage.setItem('pendingSignupRole', JSON.stringify({ role, ts: Date.now() }));
            try {
              await loginWithGoogle('/dashboard');
            } catch (error) {
              toast.error(t('auth.googleOAuthFailed'));
            }
          }}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-background transition-colors"
        >
          <GoogleIcon />
          {t('landing.registerModal.googleSignUp') || 'Sign up with Google'}
        </button>

        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">{t('landing.loginModal.orEmail') || 'or continue with email'}</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first-name">{t('landing.registerModal.firstName')}</Label>
              <Input id="first-name" value={registerData.first_name}
                onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="last-name">{t('landing.registerModal.lastName')}</Label>
              <Input id="last-name" value={registerData.last_name}
                onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })} required />
            </div>
          </div>
          <div>
            <Label htmlFor="company-name">{t('landing.registerModal.companyName')}</Label>
            <Input id="company-name" value={registerData.company_name}
              onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
              required={role === 'business'} />
          </div>
          <div>
            <Label htmlFor="register-email">{t('landing.registerModal.workEmail')}</Label>
            <Input id="register-email" type="email" value={registerData.email}
              onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              required placeholder="you@company.com" />
          </div>
          <div>
            <Label htmlFor="register-password">{t('landing.registerModal.password')}</Label>
            <div className="relative mt-1">
              <Input id="register-password" type={showPassword ? 'text' : 'password'} value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                required minLength={6} className="pr-10" />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                tabIndex={-1} data-testid="toggle-modal-register-password">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading
              ? <span className="flex items-center justify-center"><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>{t('landing.registerModal.creating')}</span>
              : t('landing.registerModal.createAccount')}
          </Button>
          <p className="text-center text-sm text-slate-600">
            {t('landing.registerModal.haveAccount')}{' '}
            <button type="button" onClick={onLoginClick} className="text-primary hover:underline font-semibold">
              {t('landing.registerModal.signInLink')}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LandingRegisterModal;
